import React, { useEffect, useState, forwardRef, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getHeatmap } from '../../../../api/reportApi';
import { getCampaign } from '../../../../api/campaignApi';
import { Campaign } from '../../../../types/campaign';
import { AdType } from '../../../../types/order';
import Select from 'react-select';
import { createOption } from '../../../../helpers/selectHelpers';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import h337 from 'heatmap.js';

interface HeatmapCardProps {
  campaignId: string;
}

export interface HeatmapCardRef {
  refresh: () => void;
}

const HeatmapCard = forwardRef<HeatmapCardRef, HeatmapCardProps>(
  ({ campaignId }, ref) => {
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [heatmapData, setHeatmapData] = useState<any>({});
    const [heatMapOptions, setHeatMapOptions] = useState<any[]>([]);
    const [heatMapSelectedOption, setHeatMapSelectedOption] = useState<any>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<string>('None');
    const [intensitySlider, setIntensitySlider] = useState<number>(100);
    const heatmapStageRef = useRef<HTMLDivElement | null>(null);
    const [heatmapScale, setHeatmapScale] = useState(1);
    const [metrics, setMetrics] = useState({
      totalInteractions: 0,
      averageEngagementTime: 0,
      clickRate: 0,
      attentionScore: 0
    });

    const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch campaign data
        const campaignData = await getCampaign(parseInt(campaignId));
        setCampaign(campaignData);

        // Fetch heatmap data
        const results = await getHeatmap(parseInt(campaignId));
        const _heatmapData: any = {};

        results.forEach((value: any) => {
          const action: string = value.action;
          const orderId = Number(value.orderId);
          if (!_heatmapData[orderId]) {
            _heatmapData[orderId] = {};
          }
          if (!_heatmapData[orderId][action]) {
            _heatmapData[orderId][action] = {
              max: value._value,
              min: value._value,
              data: [],
            };
          } else {
            _heatmapData[orderId][action].max = Math.max(_heatmapData[orderId][action].max, value._value);
            _heatmapData[orderId][action].min = Math.min(_heatmapData[orderId][action].min, value._value);
          }
          const dataPoint = {
            x: Number(value.x),
            y: Number(value.y),
            value: value._value,
          };
          _heatmapData[orderId][action].data.push(dataPoint);
        });

        setHeatmapData(_heatmapData);
      } catch (err) {
        console.error('Error fetching heatmap data:', err);
        setError('Erro ao carregar dados do mapa de calor');
      } finally {
        setLoading(false);
      }
    }, [campaignId]);

    useEffect(() => {
      fetchData();
    }, []);

    useEffect(() => {
      const orderId = Object.keys(heatmapData)[0];
      const _selectedOption = heatMapOptions.find((option: any) => option.value === orderId);
      if (_selectedOption) {
        setHeatMapSelectedOption(_selectedOption);
      }
    }, [heatmapData, heatMapOptions]);

    useEffect(() => {
      const orders = campaign?.orders;
      if (orders) {
        const _heatmapOptions = orders.reduce((acc: any[], order) => {
          if (order.creative && order.adType === AdType.DISPLAY) {
            // Apenas mostrar criativos que têm dados de heatmap disponíveis
            if (heatmapData[order.id] && Object.keys(heatmapData[order.id]).length > 0) {
              const parts = [
                `Pedido: ${order.id}`,
                `Publisher: ${order.site?.publisher?.name}`,
                order.placement ? `Bloco de anuncio: ${order.placement.name}` : null,
                order.placement ? `Tamanho: ${order.placement.sizes.join(', ')}` : null,
                `Criativo: ${order.creative?.name}`
              ].filter(Boolean);

              acc.push(createOption(parts.join(' | '), String(order.id)));
            }
          }
          return acc;
        }, []);
        setHeatMapOptions(_heatmapOptions);
      }
    }, [campaign, heatmapData]);

    const updateHeatMap = (orderId: any, eventName: string, intensity?: number) => {
      const container: HTMLElement | null = document.querySelector('#creative-heatmap');
      if (!container) return;

      if (eventName === 'None') {
        container.innerHTML = '';
        return;
      }

      const eventData = heatmapData[orderId][eventName];
      if (!eventData) return;

      // Filter data based on intensity slider (inverted logic)
      const filteredData = {
        ...eventData,
        data: eventData.data.filter((point: any) => {
          const maxValue = eventData.max;
          const threshold = (intensity || intensitySlider) / 100;
          // Inverted: 100% shows all data, 0% shows only highest values
          return point.value >= maxValue * (1 - threshold);
        })
      };

      container.innerHTML = '';
      const heatmapInstance = h337.create({ container });
      heatmapInstance.setData(filteredData);
    };

    // Calculate metrics based on current data
    const calculateMetrics = useCallback(() => {
      if (!heatMapSelectedOption || !heatmapData[heatMapSelectedOption.value]) {
        // Reset metrics if no order selected
        setMetrics({
          totalInteractions: 0,
          averageEngagementTime: 0,
          clickRate: 0,
          attentionScore: 0
        });
        return;
      }

      const orderData = heatmapData[heatMapSelectedOption.value];
      let totalInteractions = 0;
      let totalClicks = 0;
      let totalMousemoves = 0;

      Object.keys(orderData).forEach(eventName => {
        if (orderData[eventName] && orderData[eventName].data) {
          const eventCount = orderData[eventName].data.length;
          totalInteractions += eventCount;
          
          if (eventName === 'click') {
            totalClicks += eventCount;
          } else if (eventName === 'mousemove') {
            totalMousemoves += eventCount;
          }
        }
      });

      // Click rate: percentage of interactions that were clicks
      const clickRate = totalInteractions > 0 ? (totalClicks / totalInteractions) * 100 : 0;
      
      // Attention score: weighted combination of mousemoves (engagement) and clicks (intent)
      // More mousemoves = more engagement, more clicks = more intent
      const mousemoveScore = Math.min(50, (totalMousemoves / 100) * 50); // max 50 points
      const clickScore = Math.min(50, (totalClicks / 10) * 50); // max 50 points
      const attentionScore = Math.round(mousemoveScore + clickScore);

      setMetrics({
        totalInteractions,
        averageEngagementTime: 0, // TODO: Implement when backend provides this data
        clickRate: Math.round(clickRate * 100) / 100,
        attentionScore: Math.min(100, attentionScore)
      });
    }, [heatMapSelectedOption, heatmapData]);

    useEffect(() => {
      calculateMetrics();
    }, [calculateMetrics]);

    const selectedOrder = campaign?.orders.find(order => order.id === Number(heatMapSelectedOption?.value));
    const creativePreviewUrl = selectedOrder?.creative?.url;
    const isImageCreative =
      selectedOrder?.creative?.mimeType?.startsWith('image/') ||
      /\.(gif|png|jpe?g|webp|bmp|svg)(\?.*)?$/i.test(creativePreviewUrl || '');
    const [width, height] = selectedOrder?.creative?.size?.split('x') || ['400', '300'];
    const originalWidth = Number(width) || 400;
    const originalHeight = Number(height) || 300;

    const updateHeatmapScale = useCallback(() => {
      const container = heatmapStageRef.current;
      if (!container || !originalWidth) return;

      const availableWidth = Math.max(1, container.clientWidth);
      const nextScale = Math.min(1, availableWidth / originalWidth);
      setHeatmapScale(nextScale);
    }, [originalWidth]);

    useEffect(() => {
      updateHeatmapScale();

      const container = heatmapStageRef.current;
      if (!container || typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(() => updateHeatmapScale());
      observer.observe(container);

      return () => observer.disconnect();
    }, [updateHeatmapScale]);

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Mapa de calor</h4>
          </CardHeader>
          <CardContent>
            <div className="text-center text-destructive">
              <i className="ri-error-warning-line mr-2"></i>
              {error}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!heatMapOptions || heatMapOptions.length === 0) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <h4 className={styles.cardTitle}>Mapa de calor</h4>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted">
              <i className="ri-information-line mr-2"></i>
              Nenhum criativo display disponível para mapa de calor
            </div>
          </CardContent>
        </Card>
      );
    }

    const scaledWidth = Math.round(originalWidth * heatmapScale);
    const scaledHeight = Math.round(originalHeight * heatmapScale);

    // Event button configurations
    const eventButtons = [
      {
        key: 'click',
        label: 'Mapa de Cliques',
        icon: 'ri-mouse-line',
        color: 'primary'
      },
      {
        key: 'mousemove',
        label: 'Movimento',
        icon: 'ri-drag-move-line',
        color: 'info'
      },
      {
        key: 'None',
        label: 'Limpar',
        icon: 'ri-close-line',
        color: 'secondary'
      }
    ];

    return (
      <Card className={`${styles.reportCard} h-full`}>
        {/* Header with title and event buttons */}
        <CardHeader className={`${styles.cardHeader} flex items-center justify-between`}>
          <div className="flex items-center">
            <div className="bg-primary bg-opacity-10 rounded-full p-2 mr-3">
              <i className="ri-fire-line text-primary text-2xl"></i>
            </div>
            <div>
              <h4 className={`${styles.cardTitle} mb-0`}>Mapa de Calor</h4>
              <small className="text-muted">Análise de interações do usuário</small>
            </div>
          </div>

          <div className="flex gap-2">
            {heatMapSelectedOption && heatmapData[heatMapSelectedOption.value] &&
              eventButtons.map((button) => {
                const hasData = button.key === 'None' ||
                  (heatmapData[heatMapSelectedOption.value] && heatmapData[heatMapSelectedOption.value][button.key]);

                if (!hasData && button.key !== 'None') return null;

                return (
                  <Button
                    key={button.key}
                    variant={selectedEvent === button.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setSelectedEvent(button.key);
                      updateHeatMap(heatMapSelectedOption.value, button.key);
                    }}
                    className="flex items-center gap-1"
                  >
                    <i className={button.icon}></i>
                    <span className="hidden md:inline">{button.label}</span>
                  </Button>
                );
              })
            }
          </div>
        </CardHeader>

        {/* Body with heatmap visualization */}
        <CardContent className="p-4">
          {/* Metrics Row */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
            <div className="text-center">
              <div className="bg-primary bg-opacity-10 rounded-full p-2 inline-flex mb-2">
                <i className="ri-mouse-line text-primary"></i>
              </div>
              <h6 className="mb-1 font-semibold">{metrics.totalInteractions.toLocaleString()}</h6>
              <small className="text-muted">Total de Interações</small>
            </div>
            <div className="text-center">
              <div className="bg-info bg-opacity-10 rounded-full p-2 inline-flex mb-2">
                <i className="ri-time-line text-info"></i>
              </div>
              <h6 className="mb-1 font-semibold">{metrics.averageEngagementTime}s</h6>
              <small className="text-muted">Tempo Médio</small>
            </div>
            <div className="text-center">
              <div className="bg-warning bg-opacity-10 rounded-full p-2 inline-flex mb-2">
                <i className="ri-cursor-line text-warning"></i>
              </div>
              <h6 className="mb-1 font-semibold">{metrics.clickRate}%</h6>
              <small className="text-muted">Taxa de Cliques</small>
            </div>
            <div className="text-center">
              <div className={`bg-${metrics.attentionScore >= 70 ? 'success' : metrics.attentionScore >= 40 ? 'warning' : 'danger'} bg-opacity-10 rounded-full p-2 inline-flex mb-2`}>
                <i className={`ri-eye-line text-${metrics.attentionScore >= 70 ? 'success' : metrics.attentionScore >= 40 ? 'warning' : 'danger'}`}></i>
              </div>
              <h6 className="mb-1 font-semibold">{metrics.attentionScore}/100</h6>
              <small className="text-muted">Pontuação de Atenção</small>
            </div>
          </div>

          {/* Heatmap and Slider Row */}
          <div className="flex items-center justify-center">
            {/* Heatmap Container */}
            <div
              ref={heatmapStageRef}
              className="flex justify-center items-center flex-grow-1"
              style={{ maxWidth: '100%', overflow: 'hidden' }}
            >
              <div
                className="relative"
                style={{
                  width: `${scaledWidth}px`,
                  height: `${scaledHeight}px`,
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                <div
                  className="relative"
                  style={{
                    width: `${originalWidth}px`,
                    height: `${originalHeight}px`,
                    transform: `scale(${heatmapScale})`,
                    transformOrigin: 'top left'
                  }}
                >
                  {creativePreviewUrl && isImageCreative && (
                    <img
                      src={creativePreviewUrl}
                      width={originalWidth}
                      height={originalHeight}
                      alt="creative-heatmap"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        border: 'none',
                        borderRadius: '8px',
                        zIndex: 1,
                        display: 'block'
                      }}
                    />
                  )}
                  {creativePreviewUrl && !isImageCreative && (
                    <iframe
                      src={creativePreviewUrl}
                      width={originalWidth}
                      height={originalHeight}
                      title="creative-heatmap"
                      scrolling="no"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        border: 'none',
                        borderRadius: '8px',
                        zIndex: 1,
                        overflow: 'hidden'
                      }}
                    />
                  )}
                  <div
                    id="creative-heatmap"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${originalWidth}px`,
                      height: `${originalHeight}px`,
                      zIndex: 2,
                      pointerEvents: 'none'
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Intensity Slider */}
            <div className="flex flex-col items-center ml-4" style={{ width: '60px' }}>
              {/* Percentage Badge */}
              <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1 mb-3">
                {intensitySlider}%
              </span>

              {/* Plus Icon */}
              <i className="ri-add-line text-muted mb-3 text-xl"></i>

              {/* Slider Container */}
              <div style={{
                width: '20px',
                height: '120px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <Input
                  type="range"
                  min="0"
                  max="100"
                  value={intensitySlider}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    setIntensitySlider(newValue);
                    if (heatMapSelectedOption && selectedEvent !== 'None') {
                      updateHeatMap(heatMapSelectedOption.value, selectedEvent, newValue);
                    }
                  }}
                  style={{
                    transform: 'rotate(-90deg)',
                    width: '120px',
                    height: '20px',
                    position: 'absolute',
                    margin: 0
                  }}
                />
              </div>

              {/* Minus Icon */}
              <i className="ri-subtract-line text-muted mt-3 text-xl"></i>
            </div>
          </div>
        </CardContent>

        {/* Footer with metrics and controls */}
        <CardFooter className="bg-muted">
          {/* Creative Selector - Full Width */}
          <div className="mb-4">
            <Select
              value={heatMapSelectedOption}
              onChange={(selectedOption: any) => {
                setHeatMapSelectedOption(selectedOption);
                setSelectedEvent('None');
                updateHeatMap(selectedOption.value, 'None');
              }}
              options={heatMapOptions}
              placeholder="Selecione um criativo..."
              className="react-select-container"
              classNamePrefix="react-select"
              menuPlacement="top"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 })
              }}
            />
          </div>
        </CardFooter>
      </Card>
    );
  }
);

HeatmapCard.displayName = 'HeatmapCard';

export default HeatmapCard;
